import {
  BasicInformationSection,
  BreakPolicySection,
  NightPolicySection,
  WorkTimeSection
} from "./common-policy-section";
import { ApprovalPolicySection } from "./approval-policy-section";
import { BasePolicySection } from "./base-policy-section";
import { FlexibleRuleSection } from "./flexible-rule-section";
import { SelectiveRuleSection } from "./selective-rule-section";
import { StaggeredRuleSection } from "./staggered-rule-section";
import { AsyncEntity } from "../work-type-shared/async-entity-picker";
import {
  WorkPolicyFormErrors,
  WorkPolicyFormValue
} from "./hooks/use-work-policy-form";
import {
  WorkPolicyRecoveryState,
  WorkPolicySaveRecovery
} from "./work-policy-save-recovery";
import {
  ActionButton,
  ActionRow,
  EditorBody,
  EditorHeader,
  EditorSurface,
  StateMessage,
  ValidationSummary
} from "./work-type-management.styles";
import { policyKindCopy, workTypeCopy } from "./work-policy-copy";
import { OperationalGuidance } from "./work-policy-guidance";
import { FormSection, PolicyContextBanner } from "./work-type-management.styles";

interface WorkPolicyEditorProps {
  presentation?: "editable" | "embedded-readonly";
  value: WorkPolicyFormValue | null;
  errors: WorkPolicyFormErrors;
  loading: boolean;
  saving: boolean;
  dirty: boolean;
  recovery: WorkPolicyRecoveryState | null;
  successMessage: string | null;
  onChange: <K extends keyof WorkPolicyFormValue>(
    field: K,
    value: WorkPolicyFormValue[K]
  ) => void;
  onWorkTypeChange?: (workType: WorkPolicyFormValue["workType"]) => void;
  onSave: () => void;
  onDelete: () => void;
  onRetry: () => void;
  searchDepartments: (query: string) => Promise<AsyncEntity[]>;
  searchEmployees: (query: string) => Promise<AsyncEntity[]>;
  confirmTargetChange: (message: string) => boolean;
  confirmDetailChange?: (message: string) => boolean;
}

const errorSectionCopy: Partial<Record<keyof WorkPolicyFormValue, string>> = {
  profileName: "기본 정보",
  dailyBaseHours: "근로시간 기준",
  weeklyBaseHours: "근로시간 기준",
  weeklyOvertimeLimit: "근로시간 기준",
  breakWorkMinutes: "휴게시간 자동 계산",
  breakMinutes: "휴게시간 자동 계산",
  nightStart: "야간근로 정책",
  nightEnd: "야간근로 정책",
  exposureStatus: "결재 신청 설정",
  targetType: "결재 신청 설정",
  selectedDepartments: "결재 신청 설정",
  selectedEmployees: "결재 신청 설정",
  effectiveStartDate: "기본 정책 적용 설정",
  effectiveEndDate: "기본 정책 적용 설정",
  staggeredStartTimes: "시차출퇴근제 추가 설정",
  baseDefaultStartTime: "시차출퇴근제 추가 설정",
  schedule: "탄력적 근로시간제 추가 설정",
  selectiveCoreStart: "선택적 근로시간제 추가 설정"
};

export const WorkPolicyEditor = ({
  presentation = "editable",
  value,
  errors,
  loading,
  saving,
  dirty,
  recovery,
  successMessage,
  onChange,
  onWorkTypeChange,
  onSave,
  onDelete,
  onRetry,
  searchDepartments,
  searchEmployees,
  confirmTargetChange,
  confirmDetailChange = () => true
}: WorkPolicyEditorProps) => {
  const embeddedReadOnly = presentation === "embedded-readonly";
  const guardedOnChange: WorkPolicyEditorProps["onChange"] = (field, nextValue) => {
    if (!embeddedReadOnly) onChange(field, nextValue);
  };
  const errorMessages = Object.values(errors).filter(
    (message): message is string => Boolean(message)
  );
  const errorSections = Object.entries(errors).reduce<Record<string, string[]>>(
    (sections, [field, message]) => {
      if (!message) return sections;
      const section = errorSectionCopy[field as keyof WorkPolicyFormValue] ?? "추가 설정";
      sections[section] = [...(sections[section] ?? []), message];
      return sections;
    },
    {}
  );
  const kindCopy = policyKindCopy[value?.policyKind ?? "APPROVAL"];

  if (loading) {
    return (
      <EditorSurface
        aria-label={embeddedReadOnly ? "읽기 전용 기본 정책 상세" : "정책 editor"}
        data-presentation={presentation}
      >
        <StateMessage role="status">정책 상세를 불러오는 중입니다.</StateMessage>
      </EditorSurface>
    );
  }

  if (!value) {
    return (
      <EditorSurface
        aria-label={embeddedReadOnly ? "읽기 전용 기본 정책 상세" : "정책 editor"}
        data-presentation={presentation}
      >
        <StateMessage>
          목록에서 정책을 선택하거나 새 정책을 시작하면 편집 내용이 여기에 표시됩니다.
        </StateMessage>
      </EditorSurface>
    );
  }

  return (
    <EditorSurface
      aria-label={embeddedReadOnly ? "읽기 전용 기본 정책 상세" : "정책 editor"}
      data-presentation={presentation}
    >
      <EditorHeader>
        <div>
          <h2>{value.workPolicyId ? value.profileName || "정책 편집" : "새 정책"}</h2>
          <strong>{kindCopy.title}</strong>
          <span>{embeddedReadOnly ? "연결된 정책 · 읽기 전용" : dirty ? "저장하지 않은 변경 있음" : "저장된 상태"}</span>
        </div>
        {!embeddedReadOnly && <ActionRow>
          <ActionButton
            type="button"
            primary
            disabled={saving}
            aria-busy={saving}
            onClick={onSave}
          >
            {saving ? "저장 확인 중" : "근무정책 저장"}
          </ActionButton>
        </ActionRow>}
      </EditorHeader>
      <EditorBody>
        <PolicyContextBanner>
          <strong>{kindCopy.short} · {workTypeCopy[value.workType]}</strong>
          <span>{value.policyKind}</span>
          <p>{kindCopy.helper}</p>
        </PolicyContextBanner>
        {errorMessages.length > 0 && (
          <ValidationSummary role="alert" aria-label="입력 오류 요약">
            <strong>입력 오류 {errorMessages.length}건</strong>
            <ul>
              {Object.entries(errorSections).map(([section, messages]) => (
                <li key={section}>
                  <strong>{section} {messages.length}건</strong>
                  <ul>{messages.map(message => <li key={message}>{message}</li>)}</ul>
                </li>
              ))}
            </ul>
          </ValidationSummary>
        )}
        <BasicInformationSection
          value={value}
          errors={errors}
          onChange={guardedOnChange}
          onWorkTypeChange={onWorkTypeChange}
          readOnly={embeddedReadOnly}
        />
        {value.policyKind === "APPROVAL" ? (
          <ApprovalPolicySection
            value={value}
            errors={errors}
            onChange={onChange}
            searchDepartments={searchDepartments}
            searchEmployees={searchEmployees}
            confirmTargetChange={confirmTargetChange}
          />
        ) : (
          <BasePolicySection value={value} errors={errors} onChange={guardedOnChange} readOnly={embeddedReadOnly} />
        )}
        <WorkTimeSection value={value} errors={errors} onChange={guardedOnChange} readOnly={embeddedReadOnly} />
        <BreakPolicySection value={value} errors={errors} onChange={guardedOnChange} readOnly={embeddedReadOnly} />
        {value.workType === "staggered" && (
          <StaggeredRuleSection value={value} errors={errors} onChange={guardedOnChange} readOnly={embeddedReadOnly} />
        )}
        {value.workType === "flexible" && (
          <FlexibleRuleSection
            value={value}
            errors={errors}
            onChange={guardedOnChange}
            confirmDetailChange={confirmDetailChange}
            readOnly={embeddedReadOnly}
          />
        )}
        {value.workType === "selective" && (
          <SelectiveRuleSection value={value} errors={errors} onChange={guardedOnChange} readOnly={embeddedReadOnly} />
        )}
        <NightPolicySection value={value} errors={errors} onChange={guardedOnChange} readOnly={embeddedReadOnly} />
        <FormSection>
          <legend>적용·생성·통계 기준</legend>
          <OperationalGuidance />
        </FormSection>
        {!embeddedReadOnly && <FormSection>
          <legend>복구 및 삭제</legend>
          <WorkPolicySaveRecovery recovery={recovery} successMessage={successMessage} busy={saving} onRetry={onRetry} />
          {value.workPolicyId !== undefined ? (
            <ActionRow>
              <ActionButton type="button" danger disabled={saving} onClick={onDelete}>
                정책 삭제
              </ActionButton>
            </ActionRow>
          ) : (
            <p>정책을 저장한 뒤 이 영역에서 삭제할 수 있습니다.</p>
          )}
        </FormSection>}
      </EditorBody>
    </EditorSurface>
  );
};
